import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AsyncStatus,
  GroupedCashFlow,
  TransactionCategory,
  TransactionType,
  FilterStatement,
} from '@algoan/dashboard/model';
import { updateAuthInformation } from './auth-slice';
import { CheckedAccount } from './account-slice';

export interface DashboardPageReducerState {
  filters: FilterStatement;
  checkedCashFlows: { [key: number]: boolean };
  refreshInProgress: AsyncStatus;
  previousCustomerId: string | undefined;
}

const getInitialState = (): DashboardPageReducerState => ({
  filters: {
    accounts: [],
    cashFlowsCategory: [],
    cashFlowsTypes: [],
  },
  checkedCashFlows: {},
  refreshInProgress: AsyncStatus.IDLE,
  previousCustomerId: undefined,
});

const initialState = getInitialState();

const dashboardPageSlice = createSlice({
  name: 'dashboardPage',
  initialState,
  reducers: {
    filterStatementsByAccount(state, action: PayloadAction<CheckedAccount[]>) {
      state.filters.accounts = action.payload.length
        ? action.payload.map((account) => {
            return account.id;
          })
        : [];
    },
    filterStatementsByTransactionCategory(state, action: PayloadAction<TransactionCategory>) {
      if (state.filters.cashFlowsCategory.includes(action.payload)) {
        state.filters.cashFlowsCategory.splice(state.filters.cashFlowsCategory.indexOf(action.payload), 1);
      } else {
        state.filters.cashFlowsCategory.push(action.payload);
      }
    },
    filterStatementsByTransactionType(state, action: PayloadAction<TransactionType>) {
      if (state.filters.cashFlowsTypes.includes(action.payload)) {
        state.filters.cashFlowsTypes.splice(state.filters.cashFlowsTypes.indexOf(action.payload), 1);
      } else {
        state.filters.cashFlowsTypes.push(action.payload);
      }
    },
    filterStatementsByCategoryFilter(state, action: PayloadAction<TransactionCategory[]>) {
      state.filters.cashFlowsCategory = state.filters.cashFlowsCategory.filter(
        (cashFlowCategory) => !action.payload.includes(cashFlowCategory),
      );
    },

    filterStatementsByTypeFilter(state, action: PayloadAction<TransactionType[]>) {
      state.filters.cashFlowsTypes = state.filters.cashFlowsTypes.filter(
        (cashFlowType) => !action.payload.includes(cashFlowType),
      );
    },
    filterStatementsByTransactionCategories(state, action: PayloadAction<TransactionCategory[]>) {
      action.payload.forEach((cashFlowType) => {
        if (!state.filters.cashFlowsCategory.includes(cashFlowType)) {
          state.filters.cashFlowsCategory.push(cashFlowType);
        }
      });
    },
    filterStatementsByTransactionTypes(state, action: PayloadAction<TransactionType[]>) {
      action.payload.forEach((cashFlowType) => {
        if (!state.filters.cashFlowsTypes.includes(cashFlowType)) {
          state.filters.cashFlowsTypes.push(cashFlowType);
        }
      });
    },
    filterStatementsByTransactionDays(state, action: PayloadAction<FilterStatement['transactionDays']>) {
      if (action.payload === undefined || action.payload === state.filters.transactionDays) {
        state.filters.transactionDays = undefined;

        return;
      }

      state.filters.transactionDays = action.payload;
    },
    deleteCategoryTypeDateFilters(state) {
      state.filters.cashFlowsCategory = [];
      state.filters.cashFlowsTypes = [];
      state.filters.transactionDays = undefined;
    },
    deleteDateFilter(state) {
      state.filters.transactionDays = undefined;
    },
    setTransationCategoryFilter(state, action: PayloadAction<TransactionCategory[]>) {
      state.filters.accounts = [];
      state.filters.cashFlowsTypes = [];
      state.filters.transactionDays = undefined;
      state.filters.cashFlowsCategory = action.payload;
    },
    setTransactionTypeFilter(state, action: PayloadAction<TransactionType[]>) {
      state.filters.accounts = [];
      state.filters.cashFlowsCategory = [];
      state.filters.transactionDays = undefined;
      state.filters.cashFlowsTypes = action.payload;
    },

    toggleCashFlow(state, action: PayloadAction<number>) {
      state.checkedCashFlows[action.payload] = !state.checkedCashFlows[action.payload];
    },
    toggleCashFlowCollection(state, action: PayloadAction<GroupedCashFlow[]>) {
      const everyCashFlowsAreChecked = action.payload.every((cashFlow) => state.checkedCashFlows[cashFlow.index]);

      action.payload.forEach((cashFlow) => {
        state.checkedCashFlows[cashFlow.index] = !everyCashFlowsAreChecked;
      });
    },
    setCheckedCashFlows(state, action: PayloadAction<number[]>) {
      state.checkedCashFlows = action.payload.reduce((acc, checkIndex) => {
        return { ...acc, [checkIndex]: true };
      }, {});
    },
    refreshStart: (state) => {
      state.refreshInProgress = AsyncStatus.PENDING;
    },
    refreshFinish: (state) => {
      state.refreshInProgress = AsyncStatus.IDLE;
    },
    savePreviousCustomerId(state, action: PayloadAction<string>) {
      state.previousCustomerId = action.payload;
    },
    resetPreviousCustomerId(state) {
      state.previousCustomerId = undefined;
    },
    resetStatementPageState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(updateAuthInformation, (state, action) => {
      if (typeof action.payload === 'undefined') {
        // we clear the state on logout
        return getInitialState();
      }

      return state;
    });
  },
});

export const { reducer: dashboardPageReducer } = dashboardPageSlice;
export const {
  filterStatementsByAccount,
  filterStatementsByTransactionCategories,
  filterStatementsByTransactionCategory,
  filterStatementsByTransactionDays,
  filterStatementsByTransactionType,
  filterStatementsByTypeFilter,
  filterStatementsByCategoryFilter,
  filterStatementsByTransactionTypes,
  deleteCategoryTypeDateFilters,
  deleteDateFilter,
  toggleCashFlow,
  toggleCashFlowCollection,
  setCheckedCashFlows,
  resetStatementPageState,
  refreshStart,
  refreshFinish,
  savePreviousCustomerId,
  resetPreviousCustomerId,
} = dashboardPageSlice.actions;
